import { timeRegexAmPmFormat } from './constants';

const MAX_HOUR = 12;
const MAX_MINUTE = 59;
const MINUTES_INTERVAL = 15;

export const getTimeSuggestions = (minutesInterval: number): string[] => {
  const suggestions: string[] = [];
  const amPm = ['am', 'pm'];
  const maxIntervalMinute = MAX_MINUTE - minutesInterval + 1;

  for (const meridiem of amPm) {
    for (let minute = 0; minute <= maxIntervalMinute; minute += minutesInterval) {
      const hours = `${MAX_HOUR}`.slice(-2);
      const minutes = `0${minute}`.slice(-2);

      suggestions.push(`${hours}:${minutes} ${meridiem}`);
    }

    for (let hour = 1; hour < MAX_HOUR; hour++) {
      for (let minute = 0; minute <= maxIntervalMinute; minute += minutesInterval) {
        const hours = `${hour}`.slice(-2);
        const minutes = `0${minute}`.slice(-2);

        suggestions.push(`${hours}:${minutes} ${meridiem}`);
      }
    }
  }

  return suggestions;
};

export const TIME_SUGGESTIONS = getTimeSuggestions(MINUTES_INTERVAL);

export const checkTimeRegexValid = (time?: string): boolean => {
  if (!!time) {
    return new RegExp(timeRegexAmPmFormat).test(time);
  }
  return false;
};

export const parseIncorrectTime = (incorrectTime: string): string => {
  const timeRegex = /^([01]?[0-9]|2[0-3])(?:(?::|)([0-5][0-9]))?(?:\s*([aApP][mM]))?[^\d]*$/;

  const match = incorrectTime.match(timeRegex);

  if (!match && !!incorrectTime.replace(':', '').trim().length) {
    return '';
  }

  if (!!match) {
    const hoursStr = match[1];
    const minutesStr = match[2];

    const hours = parseInt(hoursStr, 10);
    const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;

    const correctedHours = hours % 12 === 0 ? 12 : hours % 12;

    let period = hours >= 12 ? 'pm' : 'am';
    if (incorrectTime.toLowerCase().includes('p')) {
      period = 'pm';
    } else if (incorrectTime.toLowerCase().includes('a')) {
      period = 'am';
    }

    const correctedTime = `${correctedHours.toString()}:${minutes
      .toString()
      .padStart(2, '0')} ${period}`;

    return correctedTime.toLocaleLowerCase();
  }

  return '';
};

export function findClosestTime(input: string): string | null {
  const inputMinutes = parseInputToMinutes(input);
  if (inputMinutes == null) return null;

  // Only return if an exact match exists
  for (const s of TIME_SUGGESTIONS) {
    if (parseTimeToMinutes(s) === inputMinutes) {
      return s;
    }
  }

  return null; // no suggestion if exact match not found
}

function parseInputToMinutes(input: string): number | null {
  input = input.trim().toLowerCase();

  // HHMM + optional meridiem: "1015a", "1015p"
  const hhmmMeridiem = input.match(/^(\d{1,2})(\d{2})(a|p)$/);
  if (hhmmMeridiem) {
    let hours = parseInt(hhmmMeridiem[1], 10);
    const minutes = parseInt(hhmmMeridiem[2], 10);
    const meridiem = hhmmMeridiem[3];

    if (meridiem === 'p' && hours !== 12) hours += 12;
    if (meridiem === 'a' && hours === 12) hours = 0;

    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return hours * 60 + minutes;
    }
  }

  // short meridiem: "10a" or "10p"
  const shortMeridiem = input.match(/^(\d{1,2})(a|p)$/);
  if (shortMeridiem) {
    let hours = parseInt(shortMeridiem[1], 10);
    const meridiem = shortMeridiem[2];
    if (meridiem === 'p' && hours !== 12) hours += 12;
    if (meridiem === 'a' && hours === 12) hours = 0;
    return hours * 60;
  }

  // HHMM numeric input like "1015" -> 10:15
  const hhmm = input.match(/^(\d{1,2})(\d{2})$/);
  if (hhmm) {
    const hours = parseInt(hhmm[1], 10);
    const minutes = parseInt(hhmm[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return hours * 60 + minutes;
    }
  }

  // only hours like "10"
  if (/^\d{1,2}$/.test(input)) {
    let hours = parseInt(input, 10);
    return hours * 60; // default AM
  }

  // full input like "10:30" or "10:30pm"
  const match = input.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)?$/);
  if (match) {
    let hours = parseInt(match[1], 10);
    let minutes = match[2] ? parseInt(match[2], 10) : 0;
    const meridiem = match[3];

    if (meridiem) {
      if (meridiem === 'pm' && hours !== 12) hours += 12;
      if (meridiem === 'am' && hours === 12) hours = 0;
    }

    return hours * 60 + minutes;
  }

  return null;
}

function parseTimeToMinutes(time: string): number {
  const match = time.match(/(\d+):?(\d*)\s*(am|pm)/i);
  if (!match) return NaN;

  let hours = parseInt(match[1], 10);
  let minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3].toLowerCase();

  if (meridiem === 'pm' && hours !== 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;

  return hours * 60 + minutes;
}
